import React, { useMemo, useState } from 'react';
import { Check, Square, Home } from 'lucide-react';
import { VipSearchBar, matchItems } from './VipSearch';

// Flatten every tickable item (plain lists + grouped sections) with its category name
const flatten = (categories) => categories
  .filter((c) => c.id !== 'nine-pillars')
  .flatMap((c) => {
    const groups = Array.isArray(c.groups) ? c.groups : [{ items: c.items || [] }];
    return groups.flatMap((g) => g.items.map((it) => ({
      ...it,
      title: it.label,
      where: g.title ? `${c.name} · ${g.title}` : c.name,
      haystack: [it.label, it.alt, it.desc, c.name, g.title].filter(Boolean).join(' ').toLowerCase(),
    })));
  });

export default function ChecklistSearch({ entry, categories, onToggle, onHomework, showHomework }) {
  const [query, setQuery] = useState('');
  const all = useMemo(() => flatten(categories), [categories]);
  const searching = query.trim().length > 0;
  const results = useMemo(() => (searching ? matchItems(all, query).slice(0, 40) : []), [all, query, searching]);

  return (
    <div data-testid={`journal-search-${entry.id}`}>
      <VipSearchBar
        value={query}
        onChange={setQuery}
        placeholder="Find an exercise, video or article to tick…"
        testId={`journal-search-${entry.id}-box`}
        className="mb-2"
      />
      {searching && (
        <div className="rounded-xl border border-forest/20 bg-white p-3 mb-2" data-testid={`journal-search-results-${entry.id}`}>
          {results.length === 0 ? (
            <p className="font-body text-xs text-stone-400 italic px-1">Nothing matches “{query.trim()}”.</p>
          ) : (
            <ul className="space-y-1.5">
              {results.map((it) => {
                const checked = !!entry.checklist?.[it.id];
                const hw = !!entry.homework?.[it.id];
                return (
                  <li key={`${it.where}-${it.id}`} className="flex items-start gap-2">
                    <button
                      onClick={() => onToggle(entry, it.id)}
                      className={`mt-0.5 flex-shrink-0 transition-colors ${checked ? 'text-forest' : 'text-stone-300 hover:text-forest'}`}
                      data-testid={`journal-search-check-${entry.id}-${it.id}`}
                    >
                      {checked ? <Check size={16} /> : <Square size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-body text-sm leading-snug ${checked ? 'text-forest font-medium' : 'text-stone-600'}`}>{it.label}</p>
                      <p className="font-body text-[10px] uppercase tracking-wider text-[#8a6a5a]">{it.where}</p>
                    </div>
                    {showHomework && (
                      <button
                        onClick={() => onHomework(entry, it.id)}
                        className={`mt-0.5 transition-colors ${hw ? 'text-[#8a6a5a]' : 'text-stone-300 hover:text-[#8a6a5a]'}`}
                        title="Assign as homework"
                        data-testid={`journal-search-hw-${entry.id}-${it.id}`}
                      >
                        <Home size={14} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
